import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSeoMeta, useSocialMeta } from '@/hooks/usePageTitle';
import { PublicShell } from '@/components/visual/PublicShell';
import { PageHeader } from '@/components/visual/PagePrimitives';
import { LocaleLink } from '@/components/LocaleLink';
import { Badge } from '@/components/ui/badge';
import { Clock, ArrowRight, BookOpen, Search, X, Coins, Wrench, Scale, ShieldCheck } from 'lucide-react';
import { BLOG_POSTS, BLOG_CATEGORIES, BLOG_AUTHOR, getLocalizedPost } from '@/data/blogData';

const CATEGORY_VISUAL = {
  fundamentals: { Icon: BookOpen },
  cost: { Icon: Coins },
  implementation: { Icon: Wrench },
  comparison: { Icon: Scale },
  controls: { Icon: ShieldCheck },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(dateStr, lang) {
  const localeMap = { en: 'en-US', de: 'de-DE', es: 'es-ES', fr: 'fr-FR', nl: 'nl-NL' };
  return new Date(dateStr + 'T00:00:00').toLocaleDateString(localeMap[lang] || 'en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function PostCard({ post, lang, t }) {
  const cat = BLOG_CATEGORIES[post.category];
  const visual = CATEGORY_VISUAL[post.category] || CATEGORY_VISUAL.fundamentals;
  const FallbackIcon = visual.Icon;
  return (
    <LocaleLink
      to={`/blog/${post.slug}`}
      className="group blog-card"
      data-testid={`blog-card-${post.slug}`}
    >
        {post.heroImageUrl ? <div className="blog-card-media" aria-hidden="true">
          <img
            src={post.heroImageUrl}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
          />
        </div> : <div className="blog-card-placeholder" aria-hidden="true"><FallbackIcon size={22} /><span className="text-xs">ISO 27001</span></div>}
      <div className="blog-card-content">
        <div className="blog-card-meta mb-3">
          <Badge variant="secondary" className={`text-xs font-medium ${cat?.color || ''}`}>
            {t('blogData.categories.' + post.category)}
          </Badge>
          <span className="flex items-center gap-1 text-xs text-[#8A929B]">
            <Clock className="w-3 h-3" /> {t('blog.minRead', { minutes: post.readingTime })}
          </span>
        </div>
        <h2
          className="text-lg font-bold text-[#171A1D] group-hover:text-[#527F19] transition-colors leading-snug"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          {post.title}
        </h2>
        <p className="mt-2 text-sm text-[#66707A] leading-relaxed line-clamp-3">
          {post.excerpt}
        </p>
        <div className="mt-auto pt-4 flex items-center justify-between">
          <span className="text-xs text-[#8A929B]">{formatDate(post.publishedDate, lang)}</span>
          <span className="flex items-center gap-1 text-sm font-medium text-[#527F19]">
            {t('blog.read')} <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </LocaleLink>
  );
}

export default function BlogPage() {
  const { t, i18n } = useTranslation();
  useSeoMeta(t('seo.pages.blogTitle'), t('seo.pages.blogDescription'));
  useSocialMeta({
    title: t('seo.pages.blogTitle'),
    description: t('seo.pages.blogDescription'),
    url: `https://ismscalculator.com/${i18n.language}/blog`,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [dbPosts, setDbPosts] = useState([]);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/blog/articles`, { params: { lang: i18n.language } })
      .then(res => { if (active) setDbPosts(res.data.articles || []); })
      .catch(() => { /* blog still renders the static knowledge base */ });
    return () => { active = false; };
  }, [i18n.language]);

  const cards = useMemo(() => {
    const dbCards = dbPosts.map(a => ({
      slug: a.slug, title: a.title, excerpt: a.excerpt,
      category: a.category, readingTime: a.reading_time,
      publishedDate: a.published_date, source: 'db',
      heroImageUrl: a.hero_image_url || null,
    }));
    const staticCards = BLOG_POSTS.map(p => {
      const loc = getLocalizedPost(p, i18n.language);
      return {
        slug: p.slug, title: loc.title, excerpt: loc.excerpt,
        category: p.category, readingTime: p.readingTime,
        publishedDate: p.publishedDate, source: 'static',
      };
    });
    return [...dbCards, ...staticCards].sort(
      (a, b) => (b.publishedDate || '').localeCompare(a.publishedDate || '')
    );
  }, [dbPosts, i18n.language]);

  const filteredPosts = cards.filter(post => {
    const matchesCategory = !activeCategory || post.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      post.title.toLowerCase().includes(q) ||
      (post.excerpt || '').toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "ISMS Calculator Blog",
      "description": "Expert articles on ISO 27001 implementation, certification, and information security management.",
      "url": "https://ismscalculator.com/blog",
      "publisher": { "@type": "Organization", "name": "ISMS Calculator", "email": BLOG_AUTHOR.email }
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  return (
    <PublicShell>
      <main id="main-content" tabIndex={-1} className="site-container public-main">
        <PageHeader title={t('blog.title')} description={t('blog.subtitle')} eyebrow={t('blog.badge')} testId="blog-heading" />

        {/* Search + Filter Bar */}
        <div className="mb-8 space-y-3">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A929B] pointer-events-none" />
            <input
              type="text"
              placeholder={t('blog.searchPlaceholder')}
              aria-label={t('blog.searchPlaceholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="ui-field w-full pl-10 pr-10 py-2.5 rounded-md border border-[#DDE1E5] text-sm"
              data-testid="blog-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#8A929B] hover:text-[#3A4249] transition-colors"
                data-testid="blog-search-clear"
                aria-label={t('visual.clearSearch')}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Category filter pills */}
          <div className="flex flex-wrap gap-2" data-testid="blog-category-filters">
            <button
              onClick={() => setActiveCategory(null)}
              className="blog-filter"
              aria-pressed={!activeCategory}
              data-testid="blog-filter-all"
            >
              {t('blog.filterAll')}
            </button>
            {Object.entries(BLOG_CATEGORIES).map(([key]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(activeCategory === key ? null : key)}
                className="blog-filter"
                aria-pressed={activeCategory === key}
                data-testid={`blog-filter-${key}`}
              >
                {t(`blogData.categories.${key}`)}
              </button>
            ))}
          </div>
        </div>

        {filteredPosts.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6 stagger-children" data-testid="blog-post-grid">
            {filteredPosts.map(post => (
              <PostCard key={post.slug} post={post} lang={i18n.language} t={t} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 text-[#8A929B]" data-testid="blog-no-results">
            <Search className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-base font-medium text-[#66707A]">{t('blog.noResults')}</p>
          </div>
        )}
      </main>
    </PublicShell>
  );
}
